import React, { useCallback, useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  averagePoint,
  geometryBounds,
  hotelBrandCountLines,
  hotelStats,
  isHmsMapHotel,
  mapHotelTypeLabel,
} from "./geoUtils";

const HMS_COLOR = "#3498db";
const THIRD_PARTY_COLOR = "#e74c3c";
const CAMERA_DURATION_MS = 900;
const CITY_CAMERA_DURATION_MS = 550;
const MARKER_FADE_MS = 220;

const STREET_STYLE = {
  version: 8,
  name: "osm-streets",
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    },
  },
  layers: [{ id: "osm", type: "raster", source: "osm" }],
};

const markerEl = (html) => {
  const el = document.createElement("div");
  el.innerHTML = html;
  return el.firstElementChild;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const cityMarkerHtml = (name, count) =>
  `<div class="city-marker-wrap">
    <div class="city-marker-bubble">${escapeHtml(count)}</div>
    <div class="city-marker-label">${escapeHtml(name)}</div>
  </div>`;

/** State-view city hover — same brand breakdown as India map state tooltip. */
const regionStatsPopupHtml = (title, hotels = []) => {
  const stats = hotelStats(hotels);
  const brandRows = hotelBrandCountLines(hotels)
    .map(
      ([label, count]) =>
        `<div class="detail-row"><span class="label">${escapeHtml(label)}</span><span class="value">${escapeHtml(count)}</span></div>`
    )
    .join("");
  return `<div class="popup-content">
    <h3>${escapeHtml(title)}</h3>
    <div class="popup-details">
      ${brandRows}
      <div class="detail-row"><span class="label">STBs</span><span class="stb-count">${escapeHtml(stats.stbCount)}</span></div>
    </div>
  </div>`;
};

const bindCityMarkerHoverPopup = (map, marker, lngLat, title, hotels) => {
  const popup = new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false,
    closeOnMove: false,
    anchor: "bottom",
    offset: [0, -12],
    className: "india-map-stats-popup",
    maxWidth: "300px",
  });
  const element = marker.getElement();
  const show = () => {
    popup.setLngLat(lngLat).setHTML(regionStatsPopupHtml(title, hotels)).addTo(map);
  };
  const hide = () => {
    popup.remove();
  };
  element.addEventListener("mouseenter", show);
  element.addEventListener("mouseleave", hide);
  element.addEventListener("focus", show);
  element.addEventListener("blur", hide);
  return () => {
    hide();
    element.removeEventListener("mouseenter", show);
    element.removeEventListener("mouseleave", hide);
    element.removeEventListener("focus", show);
    element.removeEventListener("blur", hide);
  };
};

let markerIdCounter = 0;

const hotelMarkerSvg = (stbCount, isHms, markerId) => {
  const gradId = `hotel-grad-${markerId}`;
  const startColor = isHms ? "#4299e1" : "#f56565";
  const endColor = isHms ? "#2b6cb0" : "#c53030";
  const count = Number(stbCount ?? 0);
  const countText = count > 999 ? `${(count / 1000).toFixed(1)}k` : String(count);
  const fontSize = countText.length <= 2 ? "14" : countText.length === 3 ? "12.5" : "11";

  return `<svg class="hotel-pin-svg" viewBox="0 0 44 54" width="44" height="54" aria-hidden="true">
    <defs>
      <linearGradient id="${gradId}" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stop-color="${startColor}" />
        <stop offset="100%" stop-color="${endColor}" />
      </linearGradient>
    </defs>
    <path
      d="M 22 52 C 20 44, 4 30, 4 20 A 18 18 0 1 1 40 20 C 40 30, 24 44, 22 52 Z"
      fill="url(#${gradId})"
      stroke="#ffffff"
      stroke-width="2.5"
      stroke-linejoin="round"
    />
    <circle cx="22" cy="19.5" r="12.5" fill="#ffffff" />
    <text
      x="22"
      y="19.5"
      text-anchor="middle"
      dominant-baseline="central"
      font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
      font-size="${fontSize}"
      font-weight="800"
      fill="#1a202c"
    >${escapeHtml(countText)}</text>
  </svg>`;
};

const hotelMarkerHtml = (hotel) => {
  const isHms = isHmsMapHotel(hotel);
  const typeLabel = mapHotelTypeLabel(hotel);
  const typeColor = isHms ? HMS_COLOR : THIRD_PARTY_COLOR;
  const hotelName = hotel.hotelname || "Hotel";
  markerIdCounter += 1;
  const markerId = markerIdCounter;

  return `<div class="hotel-marker-anchor" title="${escapeHtml(hotelName)} (${escapeHtml(typeLabel)})">
    <div class="hotel-marker-pulse" style="border-color:${typeColor}"></div>
    <div class="hotel-pin-hero">
      ${hotelMarkerSvg(hotel.STBcount, isHms, markerId)}
    </div>
    <div class="hotel-marker-label">
      <span class="hotel-marker-type-dot" style="background:${typeColor}"></span>
      <span class="hotel-marker-name-text">${escapeHtml(hotelName)}</span>
    </div>
  </div>`;
};

const COPY_ICON_SVG = `<svg class="coord-copy-icon" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>`;

const formatCoordinate = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(6) : "N/A";
};

const coordCopyRowHtml = (label, value) => {
  const display = value === "N/A" ? value : escapeHtml(value);
  const copyValue = value === "N/A" ? "" : escapeHtml(value);
  const copyButton = copyValue
    ? `<button type="button" class="coord-copy-btn" data-copy="${copyValue}" title="Copy ${escapeHtml(label)}" aria-label="Copy ${escapeHtml(label)}">${COPY_ICON_SVG}</button>`
    : "";
  return `<div class="detail-row">
    <span class="label">${escapeHtml(label)}</span>
    <span class="value-with-copy">
      <span class="value">${display}</span>
      ${copyButton}
    </span>
  </div>`;
};

const hotelPopupHtml = (hotel) => {
  const isHms = isHmsMapHotel(hotel);
  const typeClass = isHms ? "value hms" : "value ihcl";
  const typeLabel = mapHotelTypeLabel(hotel);
  const lat = formatCoordinate(hotel.lat);
  const lng = formatCoordinate(hotel.lng);
  return `<div class="popup-content">
    <h3>${escapeHtml(hotel.hotelname)}</h3>
    <div class="popup-details">
      <div class="detail-row"><span class="label">Type</span><span class="${typeClass}">${escapeHtml(typeLabel)}</span></div>
      <div class="detail-row"><span class="label">STB count</span><span class="stb-count">${escapeHtml(hotel.STBcount ?? 0)}</span></div>
      <div class="detail-row"><span class="label">Location</span><span class="value">${escapeHtml(`${hotel.city || "N/A"}, ${hotel.pinCode || "N/A"}`)}</span></div>
      ${coordCopyRowHtml("Latitude", lat)}
      ${coordCopyRowHtml("Longitude", lng)}
    </div>
  </div>`;
};

const copyTextToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

const bindPopupCopyButtons = (popup) => {
  popup.on("open", () => {
    const popupRoot = popup.getElement();
    if (!popupRoot) {
      return;
    }
    popupRoot.querySelectorAll(".coord-copy-btn").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const text = button.getAttribute("data-copy");
        if (!text) {
          return;
        }
        copyTextToClipboard(text).then(() => {
          button.classList.add("coord-copy-btn--done");
          button.setAttribute("title", "Copied");
          window.setTimeout(() => {
            button.classList.remove("coord-copy-btn--done");
            button.setAttribute("title", button.getAttribute("aria-label") || "Copy");
          }, 1500);
        });
      });
    });
  });
};

const fadeInMarkerElement = (element) => {
  if (!element) {
    return;
  }
  element.style.opacity = "0";
  element.style.transition = `opacity ${MARKER_FADE_MS}ms ease`;
  requestAnimationFrame(() => {
    element.style.opacity = "1";
  });
};

const fadeOutMarkers = (markers, onComplete) => {
  if (!markers.length) {
    onComplete();
    return;
  }
  markers.forEach((marker) => {
    const element = marker.getElement();
    if (element) {
      element.style.transition = `opacity ${MARKER_FADE_MS}ms ease`;
      element.style.opacity = "0";
    }
  });
  window.setTimeout(onComplete, MARKER_FADE_MS);
};

const removeMarkers = (markers) => {
  markers.forEach((marker) => {
    const popup = marker.getPopup();
    if (popup) {
      popup.remove();
    }
    marker.remove();
  });
  markers.length = 0;
};

/** MapLibre can leave marker/popup nodes in the container after city-level popups. */
const purgeOrphanMapOverlays = (map) => {
  const container = map?.getContainer();
  if (!container) {
    return;
  }
  container.querySelectorAll(".maplibregl-popup").forEach((node) => node.remove());
  container.querySelectorAll(".maplibregl-marker").forEach((node) => node.remove());
};

const StreetMapView = ({ level, stateFeature, cities = [], hotels = [], onCityClick }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const markerCleanupRef = useRef([]);
  const cameraTokenRef = useRef(0);
  const viewRef = useRef({ level, cities, hotels });
  const syncMarkersRef = useRef(() => {});
  const onCityClickRef = useRef(onCityClick);
  onCityClickRef.current = onCityClick;
  viewRef.current = { level, cities, hotels };

  const syncMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    if (!map.isStyleLoaded()) {
      map.once("load", () => syncMarkersRef.current());
      return;
    }

    const { level: activeLevel, cities: activeCities, hotels: activeHotels } = viewRef.current;
    markerCleanupRef.current.forEach((cleanup) => cleanup());
    markerCleanupRef.current = [];
    removeMarkers(markersRef.current);
    purgeOrphanMapOverlays(map);

    if (activeLevel === "state") {
      activeCities.forEach((city) => {
        if (!city.hotels?.length) {
          return;
        }
        const point = averagePoint(city.hotels);
        const stats = hotelStats(city.hotels);
        const lngLat = [point.lng, point.lat];
        const marker = new maplibregl.Marker({
          element: markerEl(cityMarkerHtml(city.name, stats.hotels)),
          anchor: "center",
        })
          .setLngLat(lngLat)
          .addTo(map);
        const element = marker.getElement();
        element.style.cursor = "pointer";
        element.setAttribute("tabindex", "0");
        element.addEventListener("click", () => {
          onCityClickRef.current?.(city.key);
        });
        markerCleanupRef.current.push(
          bindCityMarkerHoverPopup(map, marker, lngLat, city.name, city.hotels)
        );
        fadeInMarkerElement(element);
        markersRef.current.push(marker);
      });
      return;
    }

    if (activeLevel === "city") {
      activeHotels.forEach((hotel) => {
        const marker = new maplibregl.Marker({
          element: markerEl(hotelMarkerHtml(hotel)),
          anchor: "center",
        })
          .setLngLat([Number(hotel.lng), Number(hotel.lat)])
          .setPopup((() => {
            const popup = new maplibregl.Popup({ anchor: "bottom", offset: [0, -58] }).setHTML(
              hotelPopupHtml(hotel)
            );
            bindPopupCopyButtons(popup);
            return popup;
          })())
          .addTo(map);
        marker.getElement().style.cursor = "pointer";
        fadeInMarkerElement(marker.getElement());
        markersRef.current.push(marker);
      });
    }
  }, []);

  syncMarkersRef.current = syncMarkers;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) {
      return undefined;
    }
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STREET_STYLE,
      center: [78.96, 20.59],
      zoom: 6,
      minZoom: 4,
      maxZoom: 18,
      attributionControl: true,
      fadeDuration: 200,
    });
    const onLoad = () => {
      map.resize();
    };
    map.on("load", onLoad);
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");
    mapRef.current = map;
    return () => {
      map.off("load", onLoad);
      markerCleanupRef.current.forEach((cleanup) => cleanup());
      markerCleanupRef.current = [];
      removeMarkers(markersRef.current);
      purgeOrphanMapOverlays(map);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !stateFeature) {
      return undefined;
    }

    const cameraToken = ++cameraTokenRef.current;
    let cancelled = false;
    const isCityLevel = level === "city";

    const applyOutline = () => {
      const source = map.getSource("state-outline");
      const data = { type: "FeatureCollection", features: [stateFeature] };
      if (source) {
        source.setData(data);
      } else {
        map.addSource("state-outline", { type: "geojson", data });
        map.addLayer({
          id: "state-outline-line",
          type: "line",
          source: "state-outline",
          paint: { "line-color": "#000000", "line-width": 2 },
        });
      }
    };

    const runCameraTransition = () => {
      if (cancelled || cameraToken !== cameraTokenRef.current) {
        return;
      }

      applyOutline();
      map.stop();

      const bounds = geometryBounds(stateFeature.geometry);
      if (isCityLevel && hotels.length) {
        const hotelBounds = new maplibregl.LngLatBounds();
        hotels.forEach((hotel) => hotelBounds.extend([Number(hotel.lng), Number(hotel.lat)]));
        map.fitBounds(hotelBounds, {
          padding: 48,
          maxZoom: 15,
          duration: CITY_CAMERA_DURATION_MS,
        });
      } else {
        map.fitBounds(bounds, { padding: 40, maxZoom: 10, duration: CAMERA_DURATION_MS });
      }
    };

    const swapMarkersAndMoveCamera = () => {
      if (cancelled || cameraToken !== cameraTokenRef.current) {
        return;
      }
      syncMarkersRef.current();
      runCameraTransition();
    };

    const startTransition = () => {
      if (isCityLevel) {
        swapMarkersAndMoveCamera();
        return;
      }
      fadeOutMarkers(markersRef.current, swapMarkersAndMoveCamera);
    };

    if (map.isStyleLoaded()) {
      startTransition();
    } else {
      map.once("load", startTransition);
    }

    return () => {
      cancelled = true;
    };
  }, [stateFeature, level, hotels, cities]);

  return <div ref={containerRef} className="india-street-map" />;
};

export default StreetMapView;
